import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { locationService } from '../services';
import { Location } from '../types';
import { useAuth } from './AuthContext';
import { usePermissions } from './PermissionsContext';
import { getMyScreenPermissions } from '../services/userManagementService';

interface LocationContextType {
  locations: Location[];
  selectedLocationId: string; // 'all' or location id as string
  selectedLocation: Location | null;
  isLoading: boolean;
  error: string | null;
  setSelectedLocationId: (id: string) => void;
  refreshLocations: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

const STORAGE_KEY = 'secureview_default_location';

interface LocationProviderProps {
  children: ReactNode;
}

export const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { setLocationPermissions, setLocationLoading } = usePermissions();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationIdState] = useState<string>(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved || 'all';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    let active = true;
    let pending = false;
    const refresh = async () => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      try {
        const response = await getMyScreenPermissions(selectedLocationId);
        if (active) setLocationPermissions(response.permissions);
      } catch {
        if (active) setLocationPermissions([]);
      } finally {
        pending = false;
        if (active) setLocationLoading(false);
      }
    };
    setLocationLoading(true);
    void refresh();
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('platform-policy-saved', refresh);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('platform-policy-saved', refresh); };
  }, [isAuthenticated, authLoading, selectedLocationId, setLocationPermissions, setLocationLoading]);

  const fetchLocations = useCallback(async () => {
    // Don't fetch if auth is still loading or user not authenticated
    if (authLoading || !isAuthenticated) {
      return;
    }
    
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      return;
    }
    
    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);
      const response = await locationService.getLocations();
      const locationList = response.data || response || [];
      const validLocations: Location[] = Array.isArray(locationList) ? locationList : [];
      setLocations(validLocations);
      setSelectedLocationIdState(current => {
        if (current !== 'all' && !validLocations.some(location => String(location.ycl_id) === current)) {
          localStorage.setItem(STORAGE_KEY, 'all');
          return 'all';
        }
        return current;
      });
      hasInitializedRef.current = true;
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      setError('Failed to load locations');
      setLocations([]);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [isAuthenticated, authLoading]);

  // Only fetch locations when authentication is ready and user is authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchLocations();
    } else if (!authLoading && !isAuthenticated) {
      setLocations([]);
      setIsLoading(false);
    }
  }, [isAuthenticated, authLoading, fetchLocations]);

  // Clear locations on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setLocations([]);
      setError(null);
    }
  }, [isAuthenticated]);

  const setSelectedLocationId = (id: string) => {
    setSelectedLocationIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  };

  // Get the selected location object
  const selectedLocation = selectedLocationId === 'all' 
    ? null 
    : locations.find(loc => loc.ycl_id?.toString() === selectedLocationId) || null;

  const value: LocationContextType = {
    locations,
    selectedLocationId,
    selectedLocation,
    isLoading,
    error,
    setSelectedLocationId,
    refreshLocations: fetchLocations,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export default LocationContext;
